import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { map, includes, isEqual, isFunction } from 'lodash'
import useDeepCompareEffect from 'use-deep-compare-effect'

const List = ({ items, selectionType, defaultSelectedIndexes, component, containerType, selectionDidChange, beforeChange, className }) => {
  const [singleSelection, setSingleSelection] = useState()
  const [multipleSelection, setMultipleSelection] = useState([])
  const selectable = includes(['single', 'multiple'], selectionType)
  const ContainerType = containerType

  useDeepCompareEffect(() => {
    const singleSelect = selectionType === 'single'
    const multipleSelect = selectionType === 'multiple'

    if (singleSelect && singleSelection !== defaultSelectedIndexes) {
      setSingleSelection(defaultSelectedIndexes)
      return
    }

    if (multipleSelect && !isEqual(multipleSelection, defaultSelectedIndexes)) {
      setMultipleSelection(defaultSelectedIndexes)
      return
    }

  }, [defaultSelectedIndexes])

  const executeOnChange = (item, index) => {
    if (selectionType === 'single') {
      setSingleSelection(index)
      selectionDidChange(index)
      return
    }

    if (selectionType === 'multiple') {
      const set = new Set(multipleSelection)
      const action = set.has(index) ? 'delete' : 'add'

      set[action](index)

      setMultipleSelection([ ...set ])
      selectionDidChange([ ...set ])
      return
    }
  }

  const handleOnChange = (item, index) => {
    if (isFunction(beforeChange)) {
      beforeChange(item, index, () => {
        executeOnChange(item, index)
      })
    } else {
      executeOnChange(item, index)
    }
  }

  const renderItems = () => {
    return map(items, (item, index) => {
      const key = index
      let selected = undefined
      let onChange = () => {}

      if (selectable) {
        const singleSelect = selectionType === 'single'

        onChange = () => handleOnChange(item, index)
        selected = singleSelect ? singleSelection === index : includes(multipleSelection, index)
      }

      const attachedProps = {
        ...item,
        key,
        selectable,
        selected,
        onChange,
      }

      return React.createElement(component, attachedProps)
    })
  }

  return (
    <ContainerType className={className}>
      {renderItems()}
    </ContainerType>
  )
}

List.defaultProps = {
  items: [],
  component: undefined,
  selectionType: undefined,
  defaultSelectedIndexes: [],
  containerType: 'div',
  selectionDidChange: () => {},
  beforeChange: undefined,
}

List.propTypes = {
  items: PropTypes.array.isRequired,
  component: PropTypes.elementType.isRequired,
  selectionType: PropTypes.oneOf(['single', 'multiple']),
  defaultSelectedIndexes: PropTypes.oneOfType([ PropTypes.number, PropTypes.arrayOf(PropTypes.number) ]),
  containerType: PropTypes.string,
  selectionDidChange: PropTypes.func,
  beforeChange: PropTypes.func,
}

export default List
